import { getStrapiImagePath } from "@lib/utils/help";
import EmblaCarousel from "@modules/common/components/embla-carousel";
import { Radio } from "@mui/material";
import clsx from "clsx";
import Image from "next/image";
import { useMemo, useState } from "react";
import { BusinessSectionToggle } from "types/homepage";
import { StrapiImage } from "types/image";

// FeatureCard Component
function FeatureCard({
  icon,
  title,
  description,
  isChecked,
  onToggle,
}: {
  icon: StrapiImage;
  title: string;
  description: string;
  isChecked: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={clsx(
        "bg-lightPrimary rounded-2xl h-1/2 flex flex-col place-items-center justify-center gap-1 lg:gap-2 font-semibold px-2",
        {
          "outline-primary outline outline-2 ": isChecked,
        }
      )}
      onClick={onToggle}
    >
      <Image
        src={getStrapiImagePath(icon)}
        alt={title}
        width={35}
        height={35}
        className="lg:scale-100 scale-75 object-contain"
      />
      <h3 className="text-sm lg:text-base font-normal">{title}</h3>
      <p
        className={`text-xs lg:text-[0.9vw] text-lightGray font-normal transition-all duration-200 overflow-clip  ${
          isChecked ? "h-8" : "h-0"
        }`}
      >
        {description}
      </p>
      <label className="sr-only" htmlFor={"toggle" + title}>
        {"toggle" + title}
      </label>
      <Radio
        checked={isChecked}
        onClick={onToggle}
        id={"toggle" + title}
        className="!text-primary"
        sx={{
          padding: "0.2rem",
        }}
      ></Radio>
    </div>
  );
}

// Business Component
function Business({
  data,
}: {
  data: {
    businessSectionTitle: string;
    businessSectionText: string;
    businessSectionToggles: BusinessSectionToggle[];
  };
}) {
  const { businessSectionToggles } = data;
  // const [screen, setScreen] = useState<ScreenType>(ScreenType.Admin);
  const [selectedFeature, setSelectedFeature] = useState<number>(
    businessSectionToggles[0].id
  );

  // Find selected feature index
  const selectedFeatureIndex = useMemo(() => {
    const index = businessSectionToggles.findIndex(
      (feature) => feature.id === selectedFeature
    );

    return index;
  }, [selectedFeature]);

  return (
    <div
      id="products"
      className="flex flex-col w-full text-center justify-center text-darkGreen justify-items-center items-center  px-4"
    >
      <p className="text-sm lg:text-base border-[1px] border-primary px-2 py-0.5 rounded-md w-fit">
        {data.businessSectionTitle}
      </p>
      <h2 className="text-2xl lg:text-4xl font-semibold max-w-screen-xsmall mt-3">
        Be in control of your loyalty programs and orders with our <br />
        <span className="text-primary"> management app</span>
      </h2>

      {/* Tabs */}
      {/* <div className="flex my-4 lg:my-10 lg:text-xl bg-lightPrimary px-6 lg:px-8 py-2 lg:py-4 w-full lg:max-w-[43vw] justify-center rounded-2xl">
        <button
          className={clsx(
            "w-1/2 rounded-xl py-2 transition-all duration-200",
            screen === ScreenType.Admin ? "bg-primary text-white" : ""
          )}
          onClick={() => {
            setScreen(ScreenType.Admin);
            setSelectedFeature(features[ScreenType.Admin][0].name);
          }}
        >
          Prepit app
        </button>
        <button
          className={clsx(
            "w-1/2 rounded-xl py-2 transition-all duration-200",
            screen === ScreenType.Waiter ? "bg-primary text-white" : ""
          )}
          onClick={() => {
            setScreen(ScreenType.Waiter);
            setSelectedFeature(features[ScreenType.Waiter][0].name);
          }}
        >
          Waiter app
        </button>
      </div> */}

      {/* Feature Grid */}
      <div className="flex flex-col lg:flex-row gap-4 lg:h-[32vw] mt-10">
        {/* Left Column */}
        <div className="hidden lg:flex w-[15vw] gap-4 flex-col">
          {businessSectionToggles.slice(0, 2).map((feature) => (
            <FeatureCard
              key={feature.id}
              icon={feature.icon}
              title={feature.name}
              description={feature.description}
              isChecked={selectedFeature === feature.id}
              onToggle={() => setSelectedFeature(feature.id)}
            />
          ))}
        </div>

        {/* Center Column */}
        <div className="w-[calc(100vw-2rem)] h-[40vh] lg:h-auto lg:w-[43vw] bg-lightPrimary rounded-2xl flex items-center justify-center p-2 ">
          <div className="w-full h-full relative rounded-lg overflow-clip">
            <EmblaCarousel
              srcList={businessSectionToggles.map((feature) =>
                getStrapiImagePath(feature.image)
              )}
              imageIndex={selectedFeatureIndex} // Dynamically select the image index
              disableManualSwitching
              disableAutoPlay
              width={2048}
              height={1468}
              layout="responsive"
              loading="lazy"
            />
          </div>
        </div>

        <div className="flex gap-4 min-h-[350px] h-[40vh] lg:h-auto">
          <div className="flex lg:hidden w-1/2 lg:w-[15vw] gap-4 flex-col">
            {businessSectionToggles.slice(0, 2).map((feature) => (
              <FeatureCard
                key={feature.id}
                icon={feature.icon}
                title={feature.name}
                description={feature.description}
                isChecked={selectedFeature === feature.id}
                onToggle={() => setSelectedFeature(feature.id)}
              />
            ))}
          </div>

          {/* Right Column */}
          <div className="lg:w-[15vw] w-1/2 gap-4 flex flex-col">
            {businessSectionToggles.slice(2).map((feature) => (
              <FeatureCard
                key={feature.id}
                icon={feature.icon}
                title={feature.name}
                description={feature.description}
                isChecked={selectedFeature === feature.id}
                onToggle={() => setSelectedFeature(feature.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Business;
